import { useState, useRef, useCallback, useEffect, PointerEvent } from 'react';

import type {
  Point,
  ImagePoint,
  Selection,
  ImageBounds,
  ImageSelection,
  InitialSelection,
  SingleClickSelectionResolution
} from '../../ThreeDStockContentImageLensTypes';

import type {
  ImageSelectionPayload,
  ImageSelectionPayloadMetadata
} from '../../ThreeDStockContentImageLensTypes';
import type { HandleDirection } from '../../ThreeDStockContentImageLensTypes';

const DEFAULT_MIN_SELECTION_SIZE = 64;
const SELECTION_EMIT_DELAY_MS = 500;

function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, val));
}

function roundImagePointCoordinate(value: number, size: number): number {
  return clamp(Math.round(value), 0, Math.max(size - 1, 0));
}

function roundImageEdgeCoordinate(value: number, size: number): number {
  return clamp(Math.round(value), 0, size);
}

interface UseThreeDStockContentImageLensOptions {
  imageUrl: string;
  onSelectedImage?: (payload: ImageSelectionPayload) => void;
  minSelectionSize?: number;
  onResolveSingleClickSelection?: (
    point: ImagePoint
  ) => SingleClickSelectionResolution | null;
  initialSelection?: InitialSelection;
}

function useThreeDStockContentImageLens({
  imageUrl,
  onSelectedImage,
  minSelectionSize = DEFAULT_MIN_SELECTION_SIZE,
  onResolveSingleClickSelection,
  initialSelection
}: UseThreeDStockContentImageLensOptions) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const resizeStartSelectionRef = useRef<Selection | null>(null);
  const activeHandleRef = useRef<HandleDirection | null>(null);
  const emitSelectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const [imgLoaded, setImgLoaded] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [startPos, setStartPos] = useState<Point | null>(null);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [imageSize, setImageSize] = useState<{ w: number; h: number }>({
    w: 0,
    h: 0
  });

  // Stabilise initialSelection reference — only update when values change
  const initialSelectionRef = useRef(initialSelection);
  if (
    initialSelection?.x !== initialSelectionRef.current?.x ||
    initialSelection?.y !== initialSelectionRef.current?.y ||
    initialSelection?.width !== initialSelectionRef.current?.width ||
    initialSelection?.height !== initialSelectionRef.current?.height ||
    initialSelection?.unit !== initialSelectionRef.current?.unit ||
    initialSelection?.largestSearchResult !==
      initialSelectionRef.current?.largestSearchResult
  ) {
    initialSelectionRef.current = initialSelection;
  }
  const stableInitialSelection = initialSelectionRef.current;

  const clearEmitSelectionTimeout = useCallback(() => {
    if (!emitSelectionTimeoutRef.current) {
      return;
    }

    clearTimeout(emitSelectionTimeoutRef.current);
    emitSelectionTimeoutRef.current = null;
  }, []);

  useEffect(() => {
    clearEmitSelectionTimeout();
    setImgLoaded(false);
    setSelection(null);
  }, [imageUrl, clearEmitSelectionTimeout]);

  const getImageBounds = useCallback((): ImageBounds | null => {
    const img = imgRef.current;
    const container = containerRef.current;
    if (!img || !container) return null;
    const rect = img.getBoundingClientRect();
    const cRect = container.getBoundingClientRect();
    return {
      left: rect.left - cRect.left,
      top: rect.top - cRect.top,
      width: rect.width,
      height: rect.height
    };
  }, []);

  /**
   * Converts an InitialSelection (pixels or percent in source image space)
   * into a Selection expressed in container-relative CSS pixels.
   */
  const initialSelectionToContainerSelection = useCallback(
    (init: InitialSelection): Selection | null => {
      const bounds = getImageBounds();
      if (!bounds || !imageSize.w || !imageSize.h) return null;

      const unit = init.unit ?? 'px';

      // Normalise to 0-1 fractions of the source image
      const fracX = unit === 'percent' ? init.x : init.x / imageSize.w;
      const fracY = unit === 'percent' ? init.y : init.y / imageSize.h;
      const fracX2 =
        unit === 'percent'
          ? init.x + init.width
          : (init.x + init.width) / imageSize.w;
      const fracY2 =
        unit === 'percent'
          ? init.y + init.height
          : (init.y + init.height) / imageSize.h;

      const x1 = bounds.left + clamp(fracX, 0, 1) * bounds.width;
      const y1 = bounds.top + clamp(fracY, 0, 1) * bounds.height;
      const x2 =
        bounds.left +
        clamp(clamp(fracX2, 0, 1), clamp(fracX, 0, 1), 1) * bounds.width;
      const y2 =
        bounds.top +
        clamp(clamp(fracY2, 0, 1), clamp(fracY, 0, 1), 1) * bounds.height;

      return {
        x: x1,
        y: y1,
        w: x2 - x1,
        h: y2 - y1
      };
    },
    [getImageBounds, imageSize]
  );

  const applyDefaultSelection = useCallback(() => {
    if (!stableInitialSelection) return null;

    const converted = initialSelectionToContainerSelection(
      stableInitialSelection
    );
    if (!converted) return null;

    setSelection(converted);
    return converted;
  }, [stableInitialSelection, initialSelectionToContainerSelection]);

  const getSelectionInImage = useCallback(
    (sel: Selection): ImageSelection | null => {
      const bounds = getImageBounds();
      if (!bounds) return null;

      const scaleX = imageSize.w / bounds.width;
      const scaleY = imageSize.h / bounds.height;

      const rawX1 = (sel.x - bounds.left) * scaleX;
      const rawY1 = (sel.y - bounds.top) * scaleY;
      const rawX2 = (sel.x + sel.w - bounds.left) * scaleX;
      const rawY2 = (sel.y + sel.h - bounds.top) * scaleY;

      const x1 = roundImagePointCoordinate(rawX1, imageSize.w);
      const y1 = roundImagePointCoordinate(rawY1, imageSize.h);
      const x2 = clamp(
        roundImageEdgeCoordinate(rawX2, imageSize.w),
        x1,
        imageSize.w
      );
      const y2 = clamp(
        roundImageEdgeCoordinate(rawY2, imageSize.h),
        y1,
        imageSize.h
      );
      const width = x2 - x1;
      const height = y2 - y1;

      return {
        x1,
        y1,
        x2,
        y2,
        width,
        height,
        centerX: roundImagePointCoordinate((x1 + x2) / 2, imageSize.w),
        centerY: roundImagePointCoordinate((y1 + y2) / 2, imageSize.h)
      };
    },
    [getImageBounds, imageSize]
  );

  const getPointInImage = useCallback(
    (pos: Point): ImagePoint | null => {
      const bounds = getImageBounds();
      if (!bounds || !imageSize.w || !imageSize.h) return null;

      const isInsideImage =
        pos.x >= bounds.left &&
        pos.x <= bounds.left + bounds.width &&
        pos.y >= bounds.top &&
        pos.y <= bounds.top + bounds.height;

      if (!isInsideImage) return null;

      const scaleX = imageSize.w / bounds.width;
      const scaleY = imageSize.h / bounds.height;

      return {
        x: roundImagePointCoordinate(
          (pos.x - bounds.left) * scaleX,
          imageSize.w
        ),
        y: roundImagePointCoordinate((pos.y - bounds.top) * scaleY, imageSize.h)
      };
    },
    [getImageBounds, imageSize]
  );

  const getCroppedImageDataUrl = useCallback(
    (coords: ImageSelection): string | null => {
      const image = imgRef.current;
      const canvas = canvasRef.current;
      if (!image || !canvas) return null;
      if (coords.width < 1 || coords.height < 1) return null;

      canvas.width = coords.width;
      canvas.height = coords.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(
        image,
        coords.x1,
        coords.y1,
        coords.width,
        coords.height,
        0,
        0,
        coords.width,
        coords.height
      );
      try {
        return canvas.toDataURL('image/png');
      } catch (error) {
        return null;
      }
    },
    []
  );

  const getMinSelection = useCallback(
    (pos: Point): Selection | null => {
      const bounds = getImageBounds();
      if (!bounds) return null;
      const size = Math.max(
        8,
        Math.min(minSelectionSize, bounds.width, bounds.height)
      );
      const half = size / 2;

      const x = clamp(
        pos.x - half,
        bounds.left,
        bounds.left + bounds.width - size
      );
      const y = clamp(
        pos.y - half,
        bounds.top,
        bounds.top + bounds.height - size
      );

      return { x, y, w: size, h: size };
    },
    [getImageBounds, minSelectionSize]
  );

  const onSelectedImageRef = useRef(onSelectedImage);
  useEffect(() => {
    onSelectedImageRef.current = onSelectedImage;
  }, [onSelectedImage]);

  useEffect(() => {
    return () => {
      clearEmitSelectionTimeout();
    };
  }, [clearEmitSelectionTimeout]);

  const emitSelection = useCallback(
    (sel: Selection, options?: ImageSelectionPayloadMetadata) => {
      const coords = getSelectionInImage(sel);
      if (!coords) return;
      const dataUrl = getCroppedImageDataUrl(coords);
      const payload: ImageSelectionPayload = {
        selection: coords,
        imageDataUrl: dataUrl
      };

      if (options) {
        Object.assign(payload, options);
      }

      clearEmitSelectionTimeout();
      emitSelectionTimeoutRef.current = setTimeout(() => {
        onSelectedImageRef.current?.(payload);
        emitSelectionTimeoutRef.current = null;
      }, SELECTION_EMIT_DELAY_MS);
    },
    [clearEmitSelectionTimeout, getSelectionInImage, getCroppedImageDataUrl]
  );

  useEffect(() => {
    if (!imgLoaded) return;
    const id = setTimeout(() => {
      const sel = applyDefaultSelection();
      if (!sel) return;
      emitSelection(sel, {
        largestSearchResult: stableInitialSelection?.largestSearchResult
      });
    }, 80);
    return () => clearTimeout(id);
  }, [imgLoaded, applyDefaultSelection, emitSelection, stableInitialSelection]);

  const getContainerPos = useCallback((e: PointerEvent): Point => {
    const cRect = containerRef.current.getBoundingClientRect();
    return {
      x: e.clientX - cRect.left,
      y: e.clientY - cRect.top
    };
  }, []);

  const onPointerDown = useCallback(
    (e: PointerEvent) => {
      if (e.target instanceof Element) {
        const isHandleClick = e.target.closest('[data-lens-handle="true"]');
        if (isHandleClick) return;
      }
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      const pos = getContainerPos(e);
      setStartPos(pos);
      setIsDragging(true);
      setSelection(null);
      activeHandleRef.current = null;
      resizeStartSelectionRef.current = null;
    },
    [getContainerPos]
  );

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      if (!isDragging || !startPos) return;
      const pos = getContainerPos(e);
      const bounds = getImageBounds();
      if (!bounds) return;

      const activeHandle = activeHandleRef.current;
      if (activeHandle && resizeStartSelectionRef.current) {
        const startSelection = resizeStartSelectionRef.current;
        const minSize = 24;
        let x1 = startSelection.x;
        let y1 = startSelection.y;
        let x2 = startSelection.x + startSelection.w;
        let y2 = startSelection.y + startSelection.h;

        if (activeHandle === 'nw') {
          x1 = clamp(pos.x, bounds.left, x2 - minSize);
          y1 = clamp(pos.y, bounds.top, y2 - minSize);
        }

        if (activeHandle === 'ne') {
          x2 = clamp(pos.x, x1 + minSize, bounds.left + bounds.width);
          y1 = clamp(pos.y, bounds.top, y2 - minSize);
        }

        if (activeHandle === 'sw') {
          x1 = clamp(pos.x, bounds.left, x2 - minSize);
          y2 = clamp(pos.y, y1 + minSize, bounds.top + bounds.height);
        }

        if (activeHandle === 'se') {
          x2 = clamp(pos.x, x1 + minSize, bounds.left + bounds.width);
          y2 = clamp(pos.y, y1 + minSize, bounds.top + bounds.height);
        }

        if (activeHandle === 'n') {
          y1 = clamp(pos.y, bounds.top, y2 - minSize);
        }

        if (activeHandle === 's') {
          y2 = clamp(pos.y, y1 + minSize, bounds.top + bounds.height);
        }

        if (activeHandle === 'e') {
          x2 = clamp(pos.x, x1 + minSize, bounds.left + bounds.width);
        }

        if (activeHandle === 'w') {
          x1 = clamp(pos.x, bounds.left, x2 - minSize);
        }

        if (activeHandle === 'move') {
          const dx = pos.x - startPos.x;
          const dy = pos.y - startPos.y;
          const w = x2 - x1;
          const h = y2 - y1;
          x1 = clamp(
            startSelection.x + dx,
            bounds.left,
            bounds.left + bounds.width - w
          );
          y1 = clamp(
            startSelection.y + dy,
            bounds.top,
            bounds.top + bounds.height - h
          );
          x2 = x1 + w;
          y2 = y1 + h;
        }

        setSelection({ x: x1, y: y1, w: x2 - x1, h: y2 - y1 });
        return;
      }

      const x1 = clamp(
        Math.min(startPos.x, pos.x),
        bounds.left,
        bounds.left + bounds.width
      );
      const y1 = clamp(
        Math.min(startPos.y, pos.y),
        bounds.top,
        bounds.top + bounds.height
      );
      const x2 = clamp(
        Math.max(startPos.x, pos.x),
        bounds.left,
        bounds.left + bounds.width
      );
      const y2 = clamp(
        Math.max(startPos.y, pos.y),
        bounds.top,
        bounds.top + bounds.height
      );

      setSelection({ x: x1, y: y1, w: x2 - x1, h: y2 - y1 });
    },
    [isDragging, startPos, getContainerPos, getImageBounds]
  );

  const onPointerUp = useCallback(() => {
    setIsDragging(false);
    activeHandleRef.current = null;
    resizeStartSelectionRef.current = null;
    if (selection && selection.w > 10 && selection.h > 10) {
      emitSelection(selection);
      return;
    }
    if (startPos) {
      const pointInImage = getPointInImage(startPos);
      const resolvedSingleClickSelection =
        pointInImage && onResolveSingleClickSelection
          ? onResolveSingleClickSelection(pointInImage)
          : null;

      if (resolvedSingleClickSelection) {
        const resolvedSelection = initialSelectionToContainerSelection(
          resolvedSingleClickSelection.selection
        );

        if (resolvedSelection) {
          setSelection(resolvedSelection);
          emitSelection(
            resolvedSelection,
            resolvedSingleClickSelection.payload
          );
          return;
        }
      }

      const minSel = getMinSelection(startPos);
      if (minSel) {
        setSelection(minSel);
        emitSelection(minSel);
      }
    }
  }, [
    emitSelection,
    getMinSelection,
    getPointInImage,
    initialSelectionToContainerSelection,
    onResolveSingleClickSelection,
    selection,
    startPos
  ]);

  const onHandlePointerDown = useCallback(
    (handle: HandleDirection) => (e: PointerEvent) => {
      if (!selection) return;
      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      const pos = getContainerPos(e);
      setStartPos(pos);
      setIsDragging(true);
      activeHandleRef.current = handle;
      resizeStartSelectionRef.current = selection;
    },
    [selection, getContainerPos]
  );

  const onImgLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.currentTarget;
    setImageSize({ w: target.naturalWidth, h: target.naturalHeight });
    setImgLoaded(true);
  }, []);

  return {
    containerRef,
    imgRef,
    canvasRef,
    selection,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onHandlePointerDown,
    onImgLoad
  };
}

export default useThreeDStockContentImageLens;
